import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContent } from '../../context/ContentContext';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { homepageContent, siteContent } from '../../data/products';
import './Hero.css';

/* Full-bleed hero. SLIDES currently holds a single sharp photo, so the markup
 * renders one plain <img> — no crossfade wrapper, no dots. The rotation code
 * path below is kept intact: add a second entry to SLIDES and the interval
 * starts swapping `slide` again, and the dots block (commented out further
 * down) can be un-commented to bring the manual controls back.
 * No Ken Burns zoom: scaling up soft photos is what made them look blurry.
 * The same rule binds the background film — it is never scaled either. */

const HOLD_MS = 5200;

/* Data Saver / 2G — a decorative loop is the first thing that should go, and
 * this is a bakery whose customers mostly arrive on phones. Read once at
 * mount: navigator.connection is live, but swapping the background out from
 * under someone mid-scroll is worse than honouring what they arrived with. */
const prefersLightData = () => {
  const conn = typeof navigator !== 'undefined' && navigator.connection;
  return Boolean(conn && (conn.saveData === true || /2g/.test(conn.effectiveType || '')));
};

const Hero = () => {
  const { content } = useContent();
  const reduce = useReducedMotion();
  const HERO = homepageContent.hero;
  // The storefront adapter is mutable; a CMS document change invalidates the
  // slideshow even if its stable array reference has not changed.
  const SLIDES = useMemo(() => HERO.slides.length ? [...HERO.slides] : [{ image: HERO.poster, alt: HERO.posterAlt }],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content.homepage, HERO]);
  /* Lightweight branded product film, with a still fallback for reduced motion,
   * data-saver connections and browsers that cannot play either source. */
  const VIDEO_SOURCES = useMemo(() => ([
    { src: HERO.videoMobileWebm, type: 'video/webm', media: '(max-width: 767px)' },
    { src: HERO.videoMobileMp4, type: 'video/mp4', media: '(max-width: 767px)' },
    { src: HERO.videoWebm, type: 'video/webm' },
    { src: HERO.videoMp4, type: 'video/mp4' }
  ].filter((source) => source.src)), [HERO.videoMobileWebm, HERO.videoMobileMp4, HERO.videoWebm, HERO.videoMp4]);
  const [slide, setSlide] = useState(0);
  const rotating = SLIDES.length > 1;

  /* Layered 3D parallax: photo, copy and pill each ride their own plane */
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 42]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 82]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  useEffect(() => {
    SLIDES.forEach(({ image }) => {
      const img = new Image();
      img.src = image;
      if (img.decode) img.decode().catch(() => {});
    });
    if (!rotating || reduce) return undefined;
    const timer = window.setInterval(
      () => setSlide((v) => (v + 1) % SLIDES.length),
      HOLD_MS
    );
    return () => window.clearInterval(timer);
  }, [SLIDES, reduce, rotating]);

  /* ── Background film ────────────────────────────────────────────────
   * Autoplay is opt-OUT here. When reduced motion or a metered connection
   * says no we don't render the <video> at all: no bytes fetched, no play()
   * to suppress, and no orphan pause button hanging in the corner. */
  const videoRef = useRef(null);
  const [lightData] = useState(prefersLightData);
  const [compactViewport] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  ));
  const [mobileVideoRequested, setMobileVideoRequested] = useState(false);
  const hasVideo = HERO.videoEnabled && VIDEO_SOURCES.length > 0 && !reduce && !lightData;
  const mobileVideoDeferred = hasVideo && compactViewport && !mobileVideoRequested;
  const allowVideo = hasVideo && (!compactViewport || mobileVideoRequested);

  /* `ready` is the only thing that reveals the video, and it is set purely by
   * the browser telling us it can play. `failed` latches on the error event
   * so we stop poking a source that is not there. */
  const [ready, setReady] = useState(false);
  const [metadataReady, setMetadataReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);

  /* Wait for metadata before the first play() call. Calling play while React
   * is still attaching <source> nodes can reject once and never retry. */
  const shouldPlay = allowVideo && !failed && onScreen && tabVisible && !userPaused;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (shouldPlay && metadataReady) {
      /* React sets `muted` as a property, not an attribute — belt and braces,
         because an unmuted video is refused by every autoplay policy. */
      video.muted = true;
      const started = video.play();
      /* A browser may block autoplay even for muted media. Keep the poster in
         place and expose a real Play control instead of leaving a dead loop. */
      if (started && started.catch) {
        started.catch((error) => {
          if (error?.name === 'NotAllowedError') {
            setReady(true);
            setUserPaused(true);
          }
        });
      }
    } else {
      video.pause();
    }
    return undefined;
  }, [metadataReady, shouldPlay]);

  /* Battery: decoding video for a hero nobody is looking at is pure drain.
     One observer on the section itself — the same node useScroll tracks. */
  useEffect(() => {
    const node = heroRef.current;
    if (!allowVideo || !node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [allowVideo]);

  /* Backgrounded tab / locked phone. Some browsers throttle this for us,
     Safari on iOS notably does not. */
  useEffect(() => {
    if (!allowVideo) return undefined;
    const sync = () => setTabVisible(!document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [allowVideo]);

  const handlePlayable = useCallback(() => setReady(true), []);
  const handleFailure = useCallback(() => {
    setFailed(true);
    setReady(false);
  }, []);

  /* Entrance ladder — lockup, headline, sub, ghost and pill each land on
   * their own beat. The delay collapses to 0 under reduced motion so the
   * copy is simply there instead of arriving in sequence. */
  const rise = (delay) => ({
    initial: { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: 'easeOut', delay: reduce ? 0 : delay }
  });

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });

  const poster = SLIDES[slide]?.image || HERO.poster;

  return (
    <section id="hero" className="hero" ref={heroRef}>
      <motion.div className="hero-slides" style={reduce ? undefined : { y: photoY }} aria-hidden="true">
        <picture className="hero-picture">
          <source media="(max-width: 767px)" srcSet={HERO.posterMobile} />
          <img
            className="hero-photo"
            src={poster}
            alt=""
            fetchPriority="high"
            decoding="async"
            draggable="false"
          />
        </picture>
        {allowVideo && (
          <motion.video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
            disablePictureInPicture
            controlsList="nodownload"
            /* Decorative: the headline over it carries all the meaning */
            aria-hidden="true"
            tabIndex={-1}
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            onCanPlay={handlePlayable}
            onPlaying={handlePlayable}
            onLoadedMetadata={() => setMetadataReady(true)}
            onError={handleFailure}
          >
            {VIDEO_SOURCES.map(({ src, type, media }) => (
              <source key={src} src={src} type={type} media={media} />
            ))}
          </motion.video>
        )}
      </motion.div>
      <div className="hero-wash" aria-hidden="true" />

      <motion.div
        className="hero-content-plane"
        style={reduce ? undefined : { y: contentY, opacity: contentOpacity }}
      >
      <div className="hero-content">
        {/* The opening animation and navbar already carry the badge. */}
        <motion.div className="hero-lockup" {...rise(0.15)}>
          <span className="hero-lockup-tagline">{siteContent.brand.tagline}</span>
        </motion.div>

        <motion.h1 className="hero-title" {...rise(0.3)}>
          <em>{HERO.headlineLineOneAccent}</em> {HERO.headlineLineOneText}<br />
          {HERO.headlineLineTwoPrefix} <em>{HERO.headlineLineTwoAccent}</em> {HERO.headlineLineTwoText}
        </motion.h1>
        <motion.p className="hero-sub" {...rise(0.5)}>
          {HERO.subtitle}
        </motion.p>
        <motion.div className="hero-actions" {...rise(0.7)}>
          <button className="hero-order" onClick={() => scrollTo(HERO.secondaryCtaTarget)}>
            {HERO.secondaryCtaLabel}
          </button>
          <button className="hero-ghost" onClick={() => scrollTo(HERO.primaryCtaTarget)}>
            {HERO.primaryCtaLabel}
          </button>
        </motion.div>
      </div>
      </motion.div>

      {/* Manual slide controls — dormant while SLIDES has a single entry.
        * Un-comment together with a second SLIDES entry; the CSS hit box on
        * .hero-dot is already a full 44px so it ships accessible.
        * Note: the dots share the bottom-right corner with .hero-video-toggle,
        * so whichever comes back second needs to be nudged out of the way.
        *
        * {rotating && (
        *   <div className="hero-dots" role="group" aria-label="Hero photos">
        *     {SLIDES.map((s, i) => (
        *       <button
        *         key={s.src}
        *         aria-current={i === slide}
        *         aria-label={`Photo ${i + 1}`}
        *         className={`hero-dot ${i === slide ? 'on' : ''}`}
        *         onClick={() => setSlide(i)}
        *       />
        *     ))}
        *   </div>
        * )}
        */}

      {/* WCAG 2.2.2 — the loop runs well past five seconds, so it has to be
        * stoppable. Top-right keeps this utility away from the order CTA and
        * scroll cue. Mounted only once `ready` is true, so a site with no
        * video file never shows a dead control. */}
      {mobileVideoDeferred && (
        <motion.button
          type="button"
          className="hero-video-toggle"
          onClick={() => setMobileVideoRequested(true)}
          aria-label="Play background video"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
            <path d="M8 5.2 19 12 8 18.8V5.2Z" fill="currentColor" />
          </svg>
        </motion.button>
      )}
      {ready && (
        <motion.button
          type="button"
          className="hero-video-toggle"
          onClick={() => setUserPaused((v) => !v)}
          aria-label={userPaused ? 'Play background video' : 'Pause background video'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
            {userPaused ? (
              <path d="M8 5.2 19 12 8 18.8V5.2Z" fill="currentColor" />
            ) : (
              <path d="M8.5 5.2h2.6v13.6H8.5V5.2Zm4.4 0h2.6v13.6h-2.6V5.2Z" fill="currentColor" />
            )}
          </svg>
        </motion.button>
      )}

      {/* Decorative scroll cue — bottom LEFT, because the pill owns centre.
        * Gentle bob on the rail, a bead pulsing inside it. Height is not a
        * transform, so MotionConfig cannot suppress it — both loops are
        * gated on `reduce` by hand. */}
      <motion.div
        className="hero-scroll-cue"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: reduce ? 0 : 1.5 }}
      >
        <motion.span
          className="hero-scroll-rail"
          animate={reduce ? undefined : { y: [0, 7, 0] }}
          transition={reduce ? undefined : { duration: 2.6, ease: 'easeInOut', repeat: Infinity }}
        >
          <motion.i
            className="hero-scroll-bead"
            animate={reduce ? undefined : { height: ['22%', '62%', '22%'] }}
            transition={reduce ? undefined : { duration: 2.6, ease: 'easeInOut', repeat: Infinity }}
          />
        </motion.span>
        <span className="hero-scroll-label">{HERO.scrollLabel}</span>
      </motion.div>

    </section>
  );
};

export default Hero;
